import React, { useEffect } from 'react';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import TagList from '@Shared.Angular/flowingly.components/TagList/TagList';
import { getService } from '@Client/runner.hooks/useService';
import IFlowModelTag from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/flowModelTag';
import ITagGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDetail';
import { Guid } from '@Shared.Angular/@types/guid';
import {
  CustomTabs,
  ICustomTab
} from '@Shared.Angular/flowingly.components/CustomTabs/CustomTabs';

export type Props = {
  title: string;
  flowmodelId?: Guid;
  procedureId?: Guid;
  onClose?: () => void;
};

export const LibraryTagsModal = ({
  title,
  flowmodelId,
  procedureId,
  onClose
}: Props) => {
  const [flowModelTags, setFlowModelTags] = React.useState<IFlowModelTag>({
    flowModelName: '',
    tags: [],
    stepTags: []
  });
  const [procedureTags, setProcedureTags] = React.useState<ITagGroupDetail[]>(
    []
  );

  const flowModelApiService = getService('flowModelApiService');
  const procedureApiService = getService('procedureApiService');
  const [selectedTabId, setSelectedTabId] = React.useState<TabId>(
    flowmodelId ? TabId.FLOW_MODEL_TAGS : TabId.PROCEDURE_TAGS
  );

  const tabs: ICustomTab[] = [
    {
      id: TabId.FLOW_MODEL_TAGS,
      label: 'Flow Model Tags',
      isHidden: !flowmodelId
    },
    { id: TabId.STEP_TAGS, label: 'Step Tags', isHidden: !flowmodelId },
    {
      id: TabId.PROCEDURE_TAGS,
      label: 'Procedure Tags',
      isHidden: !procedureId
    }
  ];

  useEffect(() => {
    getTags();
  }, []);

  const sortTagsByName = (tagGroups: ITagGroupDetail[]) => {
    tagGroups.forEach((group) => {
      if (group.tags) {
        group.tags.sort((a, b) => a.name.localeCompare(b.name));
      }
    });

    return tagGroups;
  };

  const getTags = async () => {
    if (flowmodelId) {
      const response = await flowModelApiService.getTags(flowmodelId);
      sortTagsByName(response.tags || []);
      sortTagsByName(response.stepTags || []);
      setFlowModelTags(response);
    } else if (procedureId) {
      const procedureTagGroups = await procedureApiService.getProcedureTags(
        procedureId
      );
      sortTagsByName(procedureTagGroups);
      setProcedureTags(procedureTagGroups);
    }
  };

  const tagGroupsFormatted = (tagGroups: ITagGroupDetail[]) => {
    const result: Record<string, { tagNames: string[]; color: string }> = {};
    tagGroups.forEach((group) => {
      result[group.name] = {
        tagNames: group.tags.map((tag) => tag.name),
        color: group.color
      };
    });
    return result;
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      onCancelClick={onClose}
      hideActionButton={true}
      cancelButtonText={'Close'}
      size="lg"
    >
      <div className="library-tag-container">
        <div className="library-tag-tabs">
          <CustomTabs
            tabItems={tabs}
            selectedTabId={selectedTabId}
            onChange={(newTabId) => setSelectedTabId(newTabId as TabId)}
          ></CustomTabs>
        </div>

        <div className="library-tag-content">
          {selectedTabId === TabId.FLOW_MODEL_TAGS && (
            <div>
              <TagList
                tagsByGroupName={tagGroupsFormatted(flowModelTags.tags)}
              />
            </div>
          )}
          {selectedTabId === TabId.STEP_TAGS && (
            <div>
              {' '}
              <TagList
                tagsByGroupName={tagGroupsFormatted(flowModelTags.stepTags)}
              />
            </div>
          )}
          {selectedTabId === TabId.PROCEDURE_TAGS && (
            <div>
              {' '}
              <TagList tagsByGroupName={tagGroupsFormatted(procedureTags)} />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

enum TabId {
  FLOW_MODEL_TAGS = 'flowModelTags',
  STEP_TAGS = 'stepTags',
  PROCEDURE_TAGS = 'procedureTags'
}
